/**
 * Click-through-rate overrides for the pages that already rank.
 *
 * Search Console shows a set of Pal Stars pages sitting in the top 10–15 for
 * real queries while barely being clicked (the booster-pack page had 137
 * impressions and 1 click at position 9.5). Those pages don't need new
 * content — they need a snippet that answers the query and gives a reason to
 * click: a number, a date, a "free", a "no download".
 *
 * Each entry is the exact rendered <title> and <meta name="description"> for
 * one path. Titles stay <= 60 characters and descriptions <= 155 so Google
 * shows them whole instead of truncating mid-sentence. Keep one path per entry
 * so titles stay unique (enforced by `bun run seo:titles`).
 */
export interface Snippet {
  title: string;
  description: string;
  /** Optional Open Graph / social variant when the SERP title is very terse. */
  social?: { title: string; description: string };
}

export const SNIPPET_OVERRIDES: Record<string, Snippet> = {
  // ── Top impression pages ────────────────────────────────────────────────
  "/booster-pack": {
    title: "Palworld Booster Pack Pull Rate Calculator & Tracker",
    description:
      "Free Palworld booster pack pull rate calculator and tracker. Open Dawn of Palpagos packs with real odds, official scans, card grading and 12 free daily packs.",
    social: {
      title: "Palworld Booster Pack Simulator — Free Daily Packs",
      description:
        "Open Palworld booster packs in your browser with real pull rates, card grading and 12 free packs a day.",
    },
  },
  "/bosses": {
    title: "Palworld Boss Map & Weakness List (All Levels)",
    description:
      "Every Palworld boss on one page: alpha locations by level, tower and raid HP, the element that beats each one, counter Pals and drop tables. Updated for 1.0.",
    social: {
      title: "Palworld Bosses — Alpha Map, Levels & Weaknesses",
      description: "Alpha boss locations by level, tower and raid HP, weaknesses, counter Pals and every drop.",
    },
  },
  "/map": {
    // Owns "palworld map" + "palworld interactive map": both phrases sit in the
    // title, and the description names the layers people search for.
    title: "Palworld Interactive Map: Fast Travel & Alphas",
    description:
      "Palworld interactive map of the Palpagos Islands: fast travel points, alpha spawns, dungeons, merchants and resources, filtered by region, level or element.",
  },


  // ── Promo / TCG guides that rank in the top 10 ─────────────────────────
  "/guides/palworld-tcg-promo-card-volume-1": {
    title: "Palworld Promo Pack Volume 1: Every Card & Value",
    description:
      "All Promo Card Volume 1 cards with official scans, printed card numbers, where each promo pack comes from and what collectors are paying right now.",
  },
  "/guides/palworld-tcg-pr-promo-cards": {
    title: "Palworld PR Promo Cards: Full List & How to Get",
    description:
      "Every Palworld PR promo card with official images and codes, the event or product each one came from, rarity notes and current market prices — checked weekly.",
  },
  "/guides/palworld-tcg-promo-soul-cards": {
    title: "Palworld Soul Cards: Nox, Full List & Card Values",
    description:
      "Every promo Soul card including Nox, with official scans, printed ESOUL codes, how each one is distributed, deck uses and what they sell for today.",
  },
  "/guides/lucky-shadowbeak": {
    title: "Lucky Shadowbeak: Spawn, Catch Rate & Best Build",
    description:
      "Where Lucky Shadowbeak spawns, its catch rate with each sphere, shiny odds, stats versus normal Shadowbeak, passives to breed for and its TCG card value.",
  },
  "/guides/frostallion": {
    title: "Frostallion: How to Beat, Catch & Best Mount Build",
    description:
      "Frostallion's level, HP and weakness, the fastest way to beat it solo, catch rates by sphere, mount speed numbers and the passives worth breeding in.",
  },
  "/guides/knocklem": {
    title: "Knocklem Palworld: Location, Drops & Work Suitability",
    description:
      "Knocklem's spawn location and level, element weakness, drops, work suitability, breeding combos and how it performs in the Palworld card game.",
  },
  "/guides/daedream-tcg-strategy-guide": {
    title: "Daedream TCG Guide: Combos, Counters & Card Price",
    description:
      "How to play Daedream in the Palworld card game: best deck shells, turn-by-turn combo lines, what stops it, sideboard answers and current card prices.",
  },
  "/guides/teafant-tcg-strategy-guide": {
    title: "Teafant TCG Guide: Budget Deck, Combos & Counters",
    description:
      "Teafant's role in the Palworld TCG: a budget deck list that wins games, combo lines, the matchups it beats, the ones it loses and cheap upgrade paths.",
  },
};

/** Snippet override for a path, if one exists. */
export function snippetFor(path: string): Snippet | undefined {
  return SNIPPET_OVERRIDES[path.replace(/\/+$/, "") || "/"];
}

/** Every path that carries a CTR override — used by the title-uniqueness audit. */
export const SNIPPET_PATHS = Object.keys(SNIPPET_OVERRIDES);
